import { ProductType } from "./enums/productType";

/**
 * Written-out translation keys per product type, so the keys stay searchable
 * and scripts/lint-i18n.mjs can match them (dynamic key building is not allowed).
 */
const NAME_KEYS: Record<ProductType, string> = {
  [ProductType.IP]: "Subscription.IP.Name",
  [ProductType.LightPath]: "Subscription.LightPath.Name",
  [ProductType.L2VPN]: "Subscription.L2VPN.Name",
  [ProductType.L3VPN]: "Subscription.L3VPN.Name",
  [ProductType.Firewall]: "Subscription.FW.Name",
  [ProductType.Wireless]: "Subscription.Wireless.Name",
  [ProductType.Wired]: "Subscription.Wired.Name",
  [ProductType.Port]: "Subscription.Port.Name",
  [ProductType.IPPrefixes]: "Subscription.IP_PREFIX.Name",
  [ProductType.SURFcert]: "Subscription.cert.Name",
  [ProductType.SURFdomeinen]: "Subscription.domains.Name",
  [ProductType.IoTnet]: "Subscription.iotnet.Name",
};

/** Static pages and access-rule names that reuse the Subscription.<x>.Name family. */
const PAGE_NAME_KEYS: Record<string, string> = {
  support: "Subscription.support.Name",
  maintenance: "Subscription.maintenance.Name",
  disclaimer: "Subscription.disclaimer.Name",
  notfound: "Subscription.notfound.Name",
  forbidden: "Subscription.forbidden.Name",
  "over-surf": "Subscription.over-surf.Name",
  "notification-settings": "Subscription.notification-settings.Name",
  notification_settings: "Subscription.notification_settings.Name",
};

const TILE_NAME_KEYS: Record<ProductType, string> = {
  [ProductType.IP]: "Subscription.IP.Name.Tile",
  [ProductType.LightPath]: "Subscription.LightPath.Name.Tile",
  [ProductType.L2VPN]: "Subscription.L2VPN.Name.Tile",
  [ProductType.L3VPN]: "Subscription.L3VPN.Name.Tile",
  [ProductType.Firewall]: "Subscription.FW.Name.Tile",
  [ProductType.Wireless]: "Subscription.Wireless.Name.Tile",
  [ProductType.Wired]: "Subscription.Wired.Name.Tile",
  [ProductType.Port]: "Subscription.Port.Name.Tile",
  [ProductType.IPPrefixes]: "Subscription.IP_PREFIX.Name.Tile",
  [ProductType.SURFcert]: "Subscription.cert.Name.Tile",
  [ProductType.SURFdomeinen]: "Subscription.domains.Name.Tile",
  [ProductType.IoTnet]: "Subscription.iotnet.Name.Tile",
};

const DESCRIPTION_KEYS: Record<ProductType, string> = {
  [ProductType.IP]: "Subscription.IP.Description",
  [ProductType.LightPath]: "Subscription.LightPath.Description",
  [ProductType.L2VPN]: "Subscription.L2VPN.Description",
  [ProductType.L3VPN]: "Subscription.L3VPN.Description",
  [ProductType.Firewall]: "Subscription.FW.Description",
  [ProductType.Wireless]: "Subscription.Wireless.Description",
  [ProductType.Wired]: "Subscription.Wired.Description",
  [ProductType.Port]: "Subscription.Port.Description",
  [ProductType.IPPrefixes]: "Subscription.IP_PREFIX.Description",
  [ProductType.SURFcert]: "Subscription.cert.Description",
  [ProductType.SURFdomeinen]: "Subscription.domains.Description",
  [ProductType.IoTnet]: "Subscription.iotnet.Description",
};

/** Name key for a product type, static page, or access-rule name; undefined when unknown. */
export function subscriptionNameKey(type: string): string | undefined {
  return NAME_KEYS[type as ProductType] ?? PAGE_NAME_KEYS[type];
}

/** Tile title key for a product type; falls back to the raw type so a gap stays visible. */
export function subscriptionTileNameKey(type: string): string {
  return TILE_NAME_KEYS[type as ProductType] ?? type;
}

/** Tile description key for a product type; falls back to the raw type so a gap stays visible. */
export function subscriptionDescriptionKey(type: string): string {
  return DESCRIPTION_KEYS[type as ProductType] ?? type;
}
